import type { TweenEasing } from "./Easing";

export type EasingFunction = (k: number) => number;

const PI = Math.PI;
const BACK_S = 1.70158;

function bounceOut(k: number): number {
  if (k < 1 / 2.75) return 7.5625 * k * k;
  if (k < 2 / 2.75) return 7.5625 * (k -= 1.5 / 2.75) * k + 0.75;
  if (k < 2.5 / 2.75) return 7.5625 * (k -= 2.25 / 2.75) * k + 0.9375;
  return 7.5625 * (k -= 2.625 / 2.75) * k + 0.984375;
}

const quadIn: EasingFunction = (k) => k * k;
const quadOut: EasingFunction = (k) => k * (2 - k);
const cubicIn: EasingFunction = (k) => k * k * k;
const cubicOut: EasingFunction = (k) => --k * k * k + 1;
const quartIn: EasingFunction = (k) => k * k * k * k;
const quartOut: EasingFunction = (k) => 1 - --k * k * k * k;
const quintIn: EasingFunction = (k) => k * k * k * k * k;
const quintOut: EasingFunction = (k) => --k * k * k * k * k + 1;
const sineIn: EasingFunction = (k) => 1 - Math.cos((k * PI) / 2);
const sineOut: EasingFunction = (k) => Math.sin((k * PI) / 2);
const expoIn: EasingFunction = (k) => (k === 0 ? 0 : Math.pow(1024, k - 1));
const expoOut: EasingFunction = (k) => (k === 1 ? 1 : 1 - Math.pow(2, -10 * k));
const circIn: EasingFunction = (k) => 1 - Math.sqrt(1 - k * k);
const circOut: EasingFunction = (k) => Math.sqrt(1 - --k * k);
const elasticIn: EasingFunction = (k) => {
  if (k === 0 || k === 1) return k;
  return -Math.pow(2, 10 * (k - 1)) * Math.sin(((k - 1 - 0.1) * 2 * PI) / 0.4);
};
const elasticOut: EasingFunction = (k) => {
  if (k === 0 || k === 1) return k;
  return Math.pow(2, -10 * k) * Math.sin(((k - 0.1) * 2 * PI) / 0.4) + 1;
};
const backIn: EasingFunction = (k) => k * k * ((BACK_S + 1) * k - BACK_S);
const backOut: EasingFunction = (k) => --k * k * ((BACK_S + 1) * k + BACK_S) + 1;
const bounceIn: EasingFunction = (k) => 1 - bounceOut(1 - k);

function inOut(easeIn: EasingFunction, easeOut: EasingFunction): EasingFunction {
  return (k) => (k < 0.5 ? easeIn(k * 2) / 2 : easeOut(k * 2 - 1) / 2 + 0.5);
}
function outIn(easeIn: EasingFunction, easeOut: EasingFunction): EasingFunction {
  return (k) => (k < 0.5 ? easeOut(k * 2) / 2 : easeIn(k * 2 - 1) / 2 + 0.5);
}

/** The easing table the tuning values were authored against (Cocos Creator's names and curves). */
const EASINGS: Record<TweenEasing, EasingFunction> = {
  linear: (k) => k,
  smooth: (k) => (k <= 0 ? 0 : k >= 1 ? 1 : k * k * (3 - 2 * k)),
  fade: (k) => (k <= 0 ? 0 : k >= 1 ? 1 : k * k * k * (k * (k * 6 - 15) + 10)),
  constant: () => 0,
  quadIn,
  quadOut,
  quadInOut: inOut(quadIn, quadOut),
  quadOutIn: outIn(quadIn, quadOut),
  cubicIn,
  cubicOut,
  cubicInOut: inOut(cubicIn, cubicOut),
  cubicOutIn: outIn(cubicIn, cubicOut),
  quartIn,
  quartOut,
  quartInOut: inOut(quartIn, quartOut),
  quartOutIn: outIn(quartIn, quartOut),
  quintIn,
  quintOut,
  quintInOut: inOut(quintIn, quintOut),
  quintOutIn: outIn(quintIn, quintOut),
  sineIn,
  sineOut,
  sineInOut: (k) => 0.5 * (1 - Math.cos(PI * k)),
  sineOutIn: outIn(sineIn, sineOut),
  expoIn,
  expoOut,
  expoInOut: inOut(expoIn, expoOut),
  expoOutIn: outIn(expoIn, expoOut),
  circIn,
  circOut,
  circInOut: inOut(circIn, circOut),
  circOutIn: outIn(circIn, circOut),
  elasticIn,
  elasticOut,
  elasticInOut: inOut(elasticIn, elasticOut),
  elasticOutIn: outIn(elasticIn, elasticOut),
  backIn,
  backOut,
  backInOut: inOut(backIn, backOut),
  backOutIn: outIn(backIn, backOut),
  bounceIn,
  bounceOut,
  bounceInOut: inOut(bounceIn, bounceOut),
  bounceOutIn: outIn(bounceIn, bounceOut),
};

export function resolveEasing(easing: TweenEasing | EasingFunction | undefined, fallback: TweenEasing = "quadOut"): EasingFunction {
  if (typeof easing === "function") return easing;
  return EASINGS[easing ?? fallback] ?? EASINGS[fallback];
}
